import type { ClassValue } from 'clsx';
import type { UseEmblaCarouselType } from 'embla-carousel-react';
import type { GalleryMediaT } from '../../../../../type/galleryMediaT';

import {
  ProductGalleryOpenButton,
  ProductGalleryStrip,
  ProductGalleryStripContent,
  ProductGalleryStripItem,
  ProductGalleryVariant,
} from '@vs-shop/commerce-ui/productGallery';
import { Carousel, CarouselDots } from '@vs-shop/ui/carousel';
import { clsxm } from '@vs-shop/utils';
import { useEffect, useState } from 'react';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../../utils/intl';

import { GalleryMainMedia } from './GalleryMainMedia';

type CarouselApi = UseEmblaCarouselType[1];

export type Props = {
  media: GalleryMediaT[];
  onOpen: (idx: number) => void;
  selectedIdx: number;
  setSelectedIdx: (idx: number) => void;
  alt: string;
  className?: ClassValue;
};

/**
 * Every medium as a swipeable strip, for viewports too narrow for a thumb rail
 * beside a main image.
 *
 * The carousel's own configuration is this tenant's — it loops, and its dots
 * disappear once the wide layout takes over — as is keeping the strip's position
 * and the page's selected index in step, which is what makes the strip and the
 * lightbox agree on which medium is being looked at.
 */
export function GalleryMainImageMobile({
  media,
  onOpen,
  selectedIdx,
  setSelectedIdx,
  alt,
  className,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();
  const [api, setApi] = useState<CarouselApi>();

  // Sync carousel API changes back to parent selectedIdx
  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      const currentIndex = api.selectedScrollSnap();
      if (currentIndex !== selectedIdx) {
        setSelectedIdx(currentIndex);
      }
    };

    api.on('select', onSelect);

    return () => {
      api.off('select', onSelect);
    };
  }, [api, selectedIdx, setSelectedIdx]);

  return (
    <ProductGalleryStrip className={clsxm(className)}>
      <Carousel
        opts={{
          loop: true,
        }}
        setApi={setApi}
      >
        <ProductGalleryStripContent variant={ProductGalleryVariant.INLINE}>
          {media.map((item, idx) => {
            return (
              <ProductGalleryStripItem key={idx}>
                <GalleryMainMedia
                  alt={alt}
                  media={item}
                  isActive={idx === selectedIdx}
                  preload={idx === 0}
                />
                {item.mediaType === 'image' && (
                  <ProductGalleryOpenButton
                    aria-label={intl.formatMessage({
                      id: 'component.server.page.gallery.galleryMainImageMobile.openGallery',
                      defaultMessage: 'Otevřít galerii',
                    })}
                    onClick={() => {
                      onOpen(idx);
                    }}
                  />
                )}
              </ProductGalleryStripItem>
            );
          })}
        </ProductGalleryStripContent>
        <CarouselDots
          variant="dot"
          hideFrom="lap"
          classNameDot="h-2 w-2"
          className="flex flex-wrap items-center justify-center gap-x-3 pt-8"
        />
      </Carousel>
    </ProductGalleryStrip>
  );
}
